/**
 * Variant Tag Generator
 *
 * Automatically generates AlgoRhythm-optimized tags from variant names
 * to improve searchability and recommendation effectiveness.
 */

export interface VariantTagOptions {
  variantName?: string;
  layer?: string;
  existingTags?: string[];
}

/**
 * Generates specific, searchable tags from variant names
 * Examples:
 * - "Blue Hair" → ["blue-hair", "colorful-hair", "unique-style"]
 * - "Rock Concert" → ["rock-concert", "live-performance", "energetic"]
 */
export function generateVariantTags(options: VariantTagOptions): string[] {
  const { variantName, layer, existingTags = [] } = options;

  if (!variantName || variantName.trim() === '') {
    return [];
  }

  const generatedTags: string[] = [];
  const lowerVariantName = variantName.toLowerCase();

  // Convert variant name to searchable tag format
  const baseTag = lowerVariantName
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .trim();

  if (baseTag && !existingTags.includes(baseTag)) {
    generatedTags.push(baseTag);
  }

  // Layer-specific enhancements
  if (layer === 'S') {
    // Stars layer
    generateStarsVariantTags(lowerVariantName, generatedTags, existingTags);
  } else if (layer === 'G') {
    // Songs layer
    generateSongsVariantTags(lowerVariantName, generatedTags, existingTags);
  } else if (layer === 'L') {
    // Looks layer
    generateLooksVariantTags(lowerVariantName, generatedTags, existingTags);
  } else if (layer === 'M') {
    // Moves layer
    generateMovesVariantTags(lowerVariantName, generatedTags, existingTags);
  } else if (layer === 'W') {
    // Worlds layer
    generateWorldsVariantTags(lowerVariantName, generatedTags, existingTags);
  }

  // Remove duplicates and filter out tags that already exist
  return Array.from(new Set(generatedTags)).filter(
    tag => !existingTags.includes(tag)
  );
}

function generateStarsVariantTags(
  variantName: string,
  tags: string[],
  existing: string[]
): void {
  // Hair color patterns
  const hairColors = {
    blue: ['colorful-hair', 'unique-style', 'creative-look'],
    pink: ['vibrant-hair', 'colorful-hair', 'creative-style'],
    green: ['colorful-hair', 'unique-style', 'bold-look'],
    purple: ['vibrant-hair', 'colorful-hair', 'creative-style'],
    red: ['red-hair', 'fiery-look', 'bold-style'],
    blonde: ['blonde-hair', 'classic-look'],
    brunette: ['brunette-hair', 'natural-look'],
    black: ['dark-hair', 'classic-style'],
    multi: [
      'multi-color-hair',
      'rainbow-hair',
      'colorful-hair',
      'unique-style',
    ],
  };

  // Style patterns
  const stylePatterns = {
    rock: ['rock-style', 'edgy-look', 'alternative'],
    pop: ['pop-style', 'mainstream', 'contemporary'],
    punk: ['punk-style', 'edgy-look', 'alternative', 'rebellious'],
    goth: ['goth-style', 'dark-aesthetic', 'alternative'],
    vintage: ['vintage-style', 'retro-look', 'classic'],
    modern: ['modern-style', 'contemporary', 'current'],
    elegant: ['elegant-style', 'sophisticated', 'refined'],
    casual: ['casual-style', 'relaxed-look', 'everyday'],
  };

  // Check hair color matches
  Object.entries(hairColors).forEach(([color, colorTags]) => {
    if (variantName.includes(color)) {
      colorTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });

  // Check style matches
  Object.entries(stylePatterns).forEach(([style, styleTags]) => {
    if (variantName.includes(style)) {
      styleTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });
}

function generateSongsVariantTags(
  variantName: string,
  tags: string[],
  existing: string[]
): void {
  const musicPatterns = {
    acoustic: ['acoustic-version', 'intimate', 'stripped-down'],
    remix: ['remix-version', 'electronic', 'dance-ready'],
    live: ['live-version', 'concert', 'performance'],
    cover: ['cover-version', 'tribute', 'interpretation'],
    instrumental: ['instrumental-version', 'no-vocals', 'background'],
    extended: ['extended-version', 'long-form', 'club-mix'],
    radio: ['radio-version', 'mainstream', 'commercial'],
  };

  Object.entries(musicPatterns).forEach(([pattern, patternTags]) => {
    if (variantName.includes(pattern)) {
      patternTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });
}

function generateLooksVariantTags(
  variantName: string,
  tags: string[],
  existing: string[]
): void {
  const lookPatterns = {
    summer: ['summer-look', 'seasonal', 'warm-weather'],
    winter: ['winter-look', 'seasonal', 'cold-weather'],
    formal: ['formal-wear', 'elegant', 'dressy'],
    casual: ['casual-wear', 'everyday', 'relaxed'],
    party: ['party-wear', 'celebration', 'festive'],
    beach: ['beach-wear', 'summer', 'vacation'],
    street: ['streetwear', 'urban', 'casual'],
  };

  Object.entries(lookPatterns).forEach(([pattern, patternTags]) => {
    if (variantName.includes(pattern)) {
      patternTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });
}

function generateMovesVariantTags(
  variantName: string,
  tags: string[],
  existing: string[]
): void {
  const movePatterns = {
    slow: ['slow-tempo', 'gentle', 'relaxed'],
    fast: ['fast-tempo', 'energetic', 'dynamic'],
    smooth: ['smooth-moves', 'fluid', 'graceful'],
    sharp: ['sharp-moves', 'precise', 'defined'],
    jump: ['jumping', 'athletic', 'high-energy'],
    spin: ['spinning', 'rotation', 'dynamic'],
  };

  Object.entries(movePatterns).forEach(([pattern, patternTags]) => {
    if (variantName.includes(pattern)) {
      patternTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });
}

function generateWorldsVariantTags(
  variantName: string,
  tags: string[],
  existing: string[]
): void {
  const worldPatterns = {
    night: ['nighttime', 'dark', 'evening'],
    day: ['daytime', 'bright', 'morning'],
    urban: ['city', 'metropolitan', 'modern'],
    nature: ['natural', 'outdoor', 'organic'],
    fantasy: ['fantastical', 'magical', 'imaginative'],
    futuristic: ['sci-fi', 'modern', 'advanced'],
  };

  Object.entries(worldPatterns).forEach(([pattern, patternTags]) => {
    if (variantName.includes(pattern)) {
      patternTags.forEach(tag => {
        if (!existing.includes(tag)) tags.push(tag);
      });
    }
  });
}

/**
 * Enhances existing tags with variant-specific tags
 * This is the main function to call after AI generation
 */
export function enhanceTagsWithVariantName(
  existingTags: string[],
  variantName?: string,
  layer?: string,
  starName?: string
): string[] {
  if (!variantName) {
    return existingTags;
  }

  const variantTags = generateVariantTags({
    variantName,
    layer,
    existingTags,
  });

  // Add the literal variant name as a tag (normalized)
  const normalizedVariantName = variantName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();

  if (normalizedVariantName && !existingTags.includes(normalizedVariantName)) {
    variantTags.push(normalizedVariantName);
  }

  // Add the star name as a tag if provided (for S layer)
  if (starName && layer === 'S') {
    const normalizedStarName = starName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();

    if (normalizedStarName && !existingTags.includes(normalizedStarName)) {
      variantTags.push(normalizedStarName);
    }
  }

  // Combine existing tags with new variant tags
  const enhancedTags = [...existingTags, ...variantTags];

  // Remove duplicates and return
  return Array.from(new Set(enhancedTags));
}
